import React, { useEffect, useState } from 'react';
import { BiInfoCircle } from 'react-icons/bi';
import { useSelector } from 'react-redux';

import { Tooltip } from 'antd';
import NumericInputField from 'components/common/NumericInputField';
import {
  ORG_DEFAULT_WATER_RATE_HELP,
  ORG_DEFAULT_WATER_RATE_SAVE_ERROR,
  ORG_DEFAULT_WATER_RATE_SAVED,
} from 'constants/messageConstants';
import {
  TASKS_GRID_WATER_RATE_UNIT_LABEL,
  TASKS_SHEET_DEFAULT_WATER_RATE_LABEL,
  TASKS_SHEET_WATER_RATE_PLACEHOLDER,
} from 'constants/uiConstants';

import type { RootState } from '../../../../redux/Store';

import { useOrgDefaultWaterRate } from './useOrgDefaultWaterRate';
import { useSaveOrgDefaultWaterRate } from './useSaveOrgDefaultWaterRate';

// How long the "Saved" note stays beside the field.
const SAVED_NOTE_MS = 5000;

/**
 * The organisation's default water rate, above the Tasks grid in the new
 * layout (OP-2556), as in the legacy app. New spray tasks take it; it's
 * the organisation's setting, so it applies to everyone in it. Saves on its
 * own, with a short note beside the field rather than a toast.
 */
const OrgDefaultWaterRateField: React.FC = () => {
  const organisationId = useSelector(
    (state: RootState) => state.activeOrganisationReducer.organisationId
  );
  const defaultWaterRate = useOrgDefaultWaterRate();
  const { mutate, isPending, isError } = useSaveOrgDefaultWaterRate();
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (savedAt === null) return;
    const timeout = window.setTimeout(() => setSavedAt(null), SAVED_NOTE_MS);
    return () => window.clearTimeout(timeout);
  }, [savedAt]);

  if (!organisationId) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="flex items-center gap-1 font-semibold text-gray-dark">
        {TASKS_SHEET_DEFAULT_WATER_RATE_LABEL}
        <Tooltip title={ORG_DEFAULT_WATER_RATE_HELP}>
          <span className="flex items-center text-gray-dark">
            <BiInfoCircle aria-label={ORG_DEFAULT_WATER_RATE_HELP} />
          </span>
        </Tooltip>
      </span>
      <NumericInputField
        unit={TASKS_GRID_WATER_RATE_UNIT_LABEL}
        inputClassName="h-7 w-20 rounded border border-gray-medium bg-white text-sm font-semibold text-charcoal"
        min={0}
        allowEmpty
        disabled={isPending}
        value={defaultWaterRate}
        placeholder={TASKS_SHEET_WATER_RATE_PLACEHOLDER}
        onCommit={(waterRate) => {
          if (waterRate === defaultWaterRate) return;
          setSavedAt(null);
          mutate(
            { organisationId, waterRate },
            { onSuccess: () => setSavedAt(Date.now()) }
          );
        }}
      />
      <span role="status" className="text-sm">
        {savedAt !== null && (
          <span className="text-charcoal">{ORG_DEFAULT_WATER_RATE_SAVED}</span>
        )}
        {isError && (
          <span className="text-danger-dark">
            {ORG_DEFAULT_WATER_RATE_SAVE_ERROR}
          </span>
        )}
      </span>
    </div>
  );
};

export default OrgDefaultWaterRateField;
